/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnInit} from '@angular/core';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import * as _ from 'lodash';

import template from './segment-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';
import {TargetingHelper} from '../targeting-helper';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {SegmentWithTargetingService} from '../../../components/api/resources/resource/segment.resource';
import {UserSegmentListService} from '../../../components/api/resources/resource/user-segment-list.service';
import {EarningsAccountResource} from '../../../components/api/resources/resource/earnings-account.resource';
import {SegmentResource} from '../../../components/api/resources/resource/segment.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';

const TRANSLATIONS: Record<string, {text: string; prefix: string}> = {
  CXENSE: {text: 'Cxense', prefix: ''},
  ADOBE: {text: 'Adobe', prefix: 'adobe'},
  RELAY42: {text: 'Relay 42', prefix: 'relay42'},
  ADNUNTIUS: {text: 'Adnuntius', prefix: ''},
  LYTICS: {text: 'Lytics', prefix: 'lytics'},
  GAMMA_SSP: {text: 'Gamma SSP', prefix: 'gamma_ssp'},
  PERMUTIVE: {text: 'Permutive', prefix: 'permutive'},
  SCHIBSTED: {text: 'Schibsted', prefix: ''},
  FIRST_PARTY: {text: 'First Party Data', prefix: ''},
  ADEX: {text: 'The ADEX', prefix: 'adex'}
};

const ALL_PUBLISHERS = {name: 'Across all Publishers', id: 'ALL'};

const PREFIX_REG_EX_STRINGS = _.filter(_.map(TRANSLATIONS, (trans) => trans.prefix), (p) => p).join('|');
const SEG_REF_REG_EX = new RegExp('(^' + PREFIX_REG_EX_STRINGS + ')\\.', 'g');

@Component({
  standalone: false,
  selector: 'adn-segment-targeting',
  template
})
export class SegmentTargetingComponent implements OnInit {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.userSegment;
  @Input() underModel: any;
  @Input() runningModel: any;
  @Input() pageType: string;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  inputs: any[] = [];
  templateState: any = {
    loaded: true,
    complexInput: false
  };
  list: any = {results: [], totalHits: 0, pageStart: 0, pageEnd: 0};
  searchTerm = '';
  typeOption = {selection: 'ALL'};
  publisherOption: any = {selection: ALL_PUBLISHERS};
  publishers: any[] = [];
  publishersLoaded = false;
  isMarketplace = false;
  limited = false;
  limitedType = '';
  queryDone = false;
  loaded = true;

  searchMeta: any = {
    pageSize: 50,
    currentPage: 1,
    resolved: true
  };

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      const summary = _.flatten(_.map(this.model, (item: any) => {
        return _.map(item.userSegments, 'name');
      })).join(', ');
      return {
        vm: _.cloneDeep(this.model),
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.userSegment,
        summary
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly SegmentWithTargeting: SegmentWithTargetingService,
    private readonly UserSegmentList: UserSegmentListService,
    @Inject(SegmentResource) private readonly Segment: any,
    @Inject(EarningsAccountResource) private readonly EarningsAccount: any,
    private readonly adnLog: AdnLogService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.ensureModel();
    this.isMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.templateState.complexInput = this.init();

    if (!this.tabController) {
      this.loadPublishers();
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.userSegment.widget, () => this.onTabSelect(), copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  getSimplifiedViewUserSegment(userSegment: any): any {
    return _.get(userSegment, ['userSegments', 'length']) ? userSegment.userSegments[0] : userSegment.notUserSegments[0];
  }

  runSearch(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.search(true);
  }

  search(newSearch?: boolean): void {
    this.searchMeta.resolved = false;

    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }

    const query: any = {
      filterBy: 'name;description;externalId', filterByLike: this.searchTerm || '',
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage
    };
    if (this.typeOption.selection !== 'ALL') {
      query.type = this.typeOption.selection;
    }
    if ((_.get(this.publisherOption, ['selection', 'id']) || 'ALL') !== 'ALL') {
      query.publisherIds = [this.publisherOption.selection.id];
    }

    const fillingFunction = (page: any) => {
      this.ngZone.run(() => {
        const list = page;
        list.pageEnd = Math.min(page.totalCount, query.page * query.pageSize);
        list.pageStart = ((query.page - 1) * query.pageSize) + 1;
        list.totalHits = page.totalCount;
        this.list = list;
        this.updateSelectedSearchResults();
        this.searchMeta.resolved = true;
        this.cdr.markForCheck();
      });
    };

    if (_.isArray(query.publisherIds) || !this.isMarketplace) {
      this.limited = false;
      this.Segment.ogQuery(query).$promise.then(fillingFunction);
    } else {
      this.limitedType = this.isMarketplace ? 'publisher' : 'earnings account';
      this.limited = true;
      const runningModel = _.cloneDeep(this.runningModel);
      delete runningModel[ADN_TARGETING_TYPES.semantic.targets];
      this.SegmentWithTargeting.postTargeting(runningModel, query).then(fillingFunction);
    }
  }

  changePage(page: number): void {
    if (page < 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.search();
  }

  select(segment: any): void {
    this.addEntry(segment);
    segment.selected = true;
  }

  selectOrNot(segment: any): void {
    this.addNotEntry(segment);
    segment.selected = true;
  }

  addEntry(entry: any): void {
    const us = _.cloneDeep(entry);
    us.id = this.searchToApiId(us);
    this.inputs.push({userSegments: [us], notUserSegments: []});
  }

  addNotEntry(entry: any): void {
    const us = _.cloneDeep(entry);
    us.id = this.searchToApiId(us);
    this.inputs.push({userSegments: [], notUserSegments: [us]});
  }

  addToRow(index: number, entry?: any): void {
    const theIndex = _.isFinite(index) ? index : this.inputs.length - 1;
    this.inputs[theIndex].userSegments.push(entry || {});
  }

  addNotToRow(index: number, entry?: any): void {
    const theIndex = _.isFinite(index) ? index : this.inputs.length - 1;
    this.inputs[theIndex].notUserSegments = this.inputs[theIndex].notUserSegments || [];
    this.inputs[theIndex].notUserSegments.push(entry || {});
  }

  removeFromTarget(segment?: any): void {
    segment = segment || {};
    segment.selected = false;

    let found = false;
    _.forEach(this.inputs, (input: any) => {
      ['userSegments', 'notUserSegments'].forEach((field) => {
        input[field] = _.filter(input[field], (us: any) => {
          if (!found && ((!segment.id && (!us || !us.id)) || _.get(us, 'id') === _.get(segment, 'id'))) {
            found = true;
            return false;
          }
          return true;
        });
      });
    });
    this.inputs = _.filter(this.inputs, (input: any) => {
      return input.userSegments.length > 0 || input.notUserSegments.length > 0;
    });
    (_.find(_.get(this.list, ['results'], []), ['id', this.getSegmentIdAsReference(segment.id)]) || {}).selected = false;
  }

  removeFromRow(parentIndex: number, index: number): void {
    this.inputs[parentIndex].userSegments.splice(index, 1);
    if (!this.inputs[parentIndex].userSegments.length && !this.inputs[parentIndex].notUserSegments?.length) {
      this.inputs.splice(parentIndex, 1);
    }
  }

  removeNotFromRow(parentIndex: number, index: number): void {
    this.inputs[parentIndex].notUserSegments.splice(index, 1);
    if (!this.inputs[parentIndex].userSegments.length && !this.inputs[parentIndex].notUserSegments?.length) {
      this.inputs.splice(parentIndex, 1);
    }
  }

  private onTabSelect(): void {
    if (this.queryDone) {
      return;
    }
    this.queryDone = false;
    this.loadPublishers();
    this.hydrateSegments();
    this.search();
  }

  private hydrateSegments(): void {
    const userSegmentIds = _.flatMap(this.inputs, (row: any) => {
      return _.flatMap([row.userSegments, row.notUserSegments], (userSegmentArray: any) => userSegmentArray || []);
    }).map((userSegment: any) => userSegment?.id).filter((id: any) => !!id);

    this.UserSegmentList.query(_.map(userSegmentIds)).then((page: any) => {
      this.ngZone.run(() => {
        const selSegments = page.results;

        _.forEach(this.model, (modelEntry: any) => {
          _.forEach(_.flatMap([modelEntry.userSegments, modelEntry.notUserSegments], (userSegmentArray: any) => userSegmentArray || []), (userSegment: any) => {
              if (!userSegment || !userSegment.id) {
                return;
              }
              const foundSegment = _.find(selSegments, (s: any) => s.id === userSegment.id);
              if (foundSegment) {
                userSegment.selected = true;
                userSegment.reference = this.getSegmentIdAsReference(foundSegment.id);
                userSegment.translation = TRANSLATIONS[foundSegment.dataSource]?.text || foundSegment.dataSource;
                userSegment.className = 'label-default';
                userSegment.objectState = foundSegment.objectState;
              } else {
                userSegment.reference = this.getSegmentIdAsReference(userSegment.id);
                userSegment.className = 'label-danger';
                userSegment.translation = 'Unknown';
                userSegment.objectState = 'UNINDEXED';
                this.adnLog.warn("Couldn't find selected segment in UserSegmentList", userSegment);
              }
            });
        });
        const inputs = _.cloneDeep(this.model);
        this.inputs = inputs;
        this.normalizeInputs();
        this.queryDone = true;
        this.cdr.markForCheck();
      });
    });
  }

  private loadPublishers(): void {
    if (!this.isMarketplace) {
      deferMarkForCheck(this.cdr, () => {
        this.publishersLoaded = true;
      });
      return;
    }

    const query = this.EarningsAccount.query({minimal: true});
    const applyPublishers = (page: any) => {
      this.ngZone.run(() => {
        const publishers = _.map(page.results, (publisher: any) => {
          return {name: publisher.name, id: publisher.id};
        });
        publishers.unshift(ALL_PUBLISHERS);
        this.publishers = publishers;
        this.publisherOption = {selection: ALL_PUBLISHERS};
        this.publishersLoaded = true;
        this.cdr.markForCheck();
      });
    };
    if (query && query.$promise) {
      query.$promise.then(applyPublishers);
    } else {
      this.EarningsAccount.query({minimal: true}, applyPublishers);
    }
  }

  private updateSelectedSearchResults(): void {
    const selIds = _.keyBy(_.map(this.model, (segment: any) => {
      return segment.userSegments[0];
    }), 'id');

    _.forEach(_.get(this.list, ['results'], []), (result: any) => {
      result.selected = !!selIds[result.id];
      result.reference = this.getSegmentIdAsReference(result.id);
      result.translation = TRANSLATIONS[result.dataSource]?.text || result.dataSource;
      result.className = 'label-default';
      result.publishers = _.map(result.publishers, (publisher: any) => publisher.name);
    });
  }

  private searchToApiId(entry: any): string {
    const prefix = _.get(TRANSLATIONS[entry.dataSource], ['prefix'], '');
    const prefixString = prefix + '.';
    if (prefix && !_.startsWith(entry.id, prefixString)) {
      return prefixString + entry.id;
    }
    return entry.id;
  }

  private getSegmentIdAsReference(segId: string): string {
    return (segId || '').replace(SEG_REF_REG_EX, '');
  }

  private init(): boolean {
    this.inputs = _.cloneDeep(this.model);
    this.normalizeInputs();
    return TargetingHelper.isComplexSegments(this.model);
  }

  private normalizeInputs(): void {
    _.forEach(this.inputs, (entry: any) => {
      entry.userSegments = entry.userSegments || [];
      entry.notUserSegments = entry.notUserSegments || [];
    });
  }

  private prepareForSave(): void {
    if (!this.model) {
      return;
    }
    this.model.length = 0;
    _.forEach(this.inputs, (entry: any) => {
      if (this.templateState.complexInput) {
        this.model.push(entry);
      } else {
        this.model.push(entry.userSegments.length
          ? {userSegments: [entry.userSegments[0]]}
          : {notUserSegments: [entry.notUserSegments[0]]});
      }
    });
    this.init();
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.templateState.complexInput = this.init();
    this.updateSelectedSearchResults();
  }

  private ensureModel(): void {
    this.model = this.model || [];
  }
}
